import { Injectable } from '@angular/core';
import { ModalController, Platform, NavController } from '@ionic/angular';
import { from, lastValueFrom, Subject } from 'rxjs';
import { exhaustMap, filter, first, mergeMap, tap } from 'rxjs/operators';
import { VaultService } from '../vault/vault.service';
import { PinCodeComponent } from 'src/app/components/pin-code/pin-code/pin-code.component';
import { AuthFlowService } from '../auth-flow/auth-flow.service';
import { SET_AUTO_LOCK_TRUE } from '../photo-video/photo-video.service';
import { ActivatedRoute } from '@angular/router';
import { EventBusService, EventTopic } from '../event-bus/event-bus.service';
import { AuthFlowStateEventType } from 'src/app/models/pin-code.model';

@Injectable({
  providedIn: 'root',
})
export class PinCodeService {
  private tempLock = false;
  private autoLockEnabled = true;
  viewScreenUnlocked = false;

  private unlockSubject = new Subject();
  private unlockResultSubject = new Subject<boolean>();
  private unlockResult$ = this.unlockResultSubject.pipe(first());

  private setupLockSubject = new Subject();
  private setupLockResultSubject = new Subject<boolean>();
  private setupLockResult$ = this.setupLockResultSubject.pipe(first());
  private modalCssClass = 'pincode-modal';

  constructor(
    private platform: Platform,
    private modalCtrl: ModalController,
    public vault: VaultService,
    private navCtrl: NavController,
    private authFlowSvc: AuthFlowService,
    private activatedRoute: ActivatedRoute,
    private eventBusSvc: EventBusService
  ) {
    this.initUnlock();
    this.initSetupLock();
    this.initAutoLock();
  }

  private initUnlock() {
    this.unlockSubject
      .pipe(exhaustMap(() => this.createModal(PincodeModalType.auto).pipe(tap(event => this.unlockResultSubject.next(event.data)))))
      .subscribe();
  }

  private initSetupLock() {
    this.setupLockSubject
      .pipe(
        exhaustMap(() => this.createModal(PincodeModalType.changeOrSetup).pipe(tap(event => this.setupLockResultSubject.next(event.data))))
      )
      .subscribe();
  }

  private createModal(type: PincodeModalType) {
    this.viewScreenUnlocked = false;
    return from(
      this.modalCtrl.create({
        component: PinCodeComponent,
        keyboardClose: false,
        backdropDismiss: false,
        componentProps: {
          type,
        },
        cssClass: [this.modalCssClass, ...this.platform.platforms()].join(' '),
      })
    ).pipe(
      mergeMap(modal => {
        const onDismiss$ = from(modal.onWillDismiss());
        modal.present();
        return onDismiss$;
      })
    );
  }

  setAutoLock(enable: boolean) {
    this.autoLockEnabled = enable;
    localStorage.removeItem(SET_AUTO_LOCK_TRUE);
  }

  private initAutoLock() {
    this.platform.pause.subscribe(async () => {
      if (!this.vault.state.isLocked) {
        const setAutoLock = localStorage.getItem(SET_AUTO_LOCK_TRUE);
        if (setAutoLock) {
          this.setAutoLock(true);
        }
        if (this.autoLockEnabled) {
          this.viewScreenUnlocked = false;
          this.tempLock = true;
          this.vault.lockVault();
          this.forceClosePincodeModal();
        }
      } else if (this.activatedRoute.root.firstChild?.snapshot?.data?.nopincode) {
        this.tempLock = false;
      }
      this.eventBusSvc.publish(EventTopic.AuthFlowStateEventType, { type: AuthFlowStateEventType.clearPin });
    });
    this.platform.resume.pipe(filter(() => this.autoLockEnabled && this.tempLock)).subscribe(async () => {
      this.tempLock = false;
      const unlocked = await this.showUnlockModal();
      this.viewScreenUnlocked = unlocked;
      if (!unlocked) {
        this.navCtrl.navigateRoot('landing');
      }
    });
  }

  async forceClosePincodeModal() {
    const modal = await this.modalCtrl.getTop();
    if (modal) {
      let modalCssClasses = modal.cssClass;
      if (typeof modal.cssClass === 'string') {
        modalCssClasses = modal.cssClass.split(' ');
      }
      if (modalCssClasses.includes(this.modalCssClass)) {
        modal.dismiss();
      }
    }
  }

  /**
   * resolves with true if app is already unlocked
   * show unlock modal if app is locked and resolves with unlock state whether is true or false
   * discard any subsequent invocations
   * sends the result to all invocations
   */
  async unlock() {
    if (!this.vault.state.isLocked) {
      return true;
    }
    this.viewScreenUnlocked = false;
    return await this.showUnlockModal();
  }

  private async showUnlockModal() {
    if (!(await this.authFlowSvc.isPincodeEnabled())) {
      this.vault.unlockVault();
      return new Promise<boolean>(resolve => resolve(true));
    }
    const resultPromise = lastValueFrom(this.unlockResult$);
    this.unlockSubject.next(true);
    return await resultPromise;
  }

  /**
   * show setup-lock modal and resolves with unlock state whether is true or false
   * discard any subsequent invocations
   * sends the result to all invocations
   */
  async setupLock() {
    const resultPromise = lastValueFrom(this.setupLockResult$);
    this.setupLockSubject.next(true);
    return await resultPromise;
  }
}

export enum PincodeModalType {
  auto = 'auto',
  changeOrSetup = 'changeOrSetup',
}
